<template>
  <NModal
    :bordered="false"
    class="cb-add-component-modal"
    :onEsc="() => setAddMode(false)"
    preset="card"
    show
    size="huge"
    :style="{
      '--n-border-radius': '16px',
      '--n-padding-top': '16px',
      '--n-padding-right': '16px',
      '--n-padding-bottom': '16px',
      '--n-padding-left': '16px',
    }"
    :title="addMode ? t('canvasUI.canvasBuilder.addAt', { component: getComponentTitle(addMode.type, t) }) : ''"
    :z-index="baseStackingContext"
    @close="setAddMode(false)"
    @mask-click="setAddMode(false)"
  >
    <NCard
      aria-modal="true"
      :bordered="false"
      class="cb-add-component-modal__card"
      embedded
      role="dialog"
      :style="{
        '--n-padding-top': 0,
        '--n-padding-right': 0,
        '--n-padding-bottom': 0,
        '--n-padding-left': 0,
      }"
    >
      <template #header>
        <!-- Search and filter -->
        <div class="cb-add-component-modal__card-header" :style="{ backgroundColor: themeVars.base }">
          <NInput
            ref="inputRef"
            v-model:value="search"
            class="mb-4"
            :placeholder="t('canvasUI.canvasBuilder.searchPlaceholder')"
          >
            <template #prefix>
              <CIcon icon="search" size="16px" />
            </template>
          </NInput>
          <div v-if="!hideApps || shouldShowBlocksTab" class="flex gap-2 pb-4 uppercase tracking-wider">
            <CTag
              :bordered="activeFilter === ComponentFilterTypesEnum.Basic"
              class="text-xs cursor-pointer"
              :closable="activeFilter === ComponentFilterTypesEnum.Basic"
              round
              :trigger-click-on-close="false"
              @click="handleClick(ComponentFilterTypesEnum.Basic)"
              @close="handleClose"
            >
              {{ t('canvasUI.canvasBuilder.components.categories.standard') }}
            </CTag>
            <CTag
              v-if="!hideApps"
              :bordered="activeFilter === ComponentFilterTypesEnum.Apps"
              class="text-xs cursor-pointer"
              :closable="activeFilter === ComponentFilterTypesEnum.Apps"
              round
              :trigger-click-on-close="false"
              @click="handleClick(ComponentFilterTypesEnum.Apps)"
              @close="handleClose"
            >
              {{ t('canvasUI.canvasBuilder.components.categories.apps') }}
            </CTag>
            <CTag
              v-if="shouldShowBlocksTab"
              :bordered="activeFilter === ComponentFilterTypesEnum.Blocks"
              class="text-xs cursor-pointer"
              :closable="activeFilter === ComponentFilterTypesEnum.Blocks"
              round
              :trigger-click-on-close="false"
              @click="handleClick(ComponentFilterTypesEnum.Blocks)"
              @close="handleClose"
            >
              {{ t('canvasUI.canvasBuilder.components.categories.blocks') }}
            </CTag>
          </div>
        </div>
        <div class="flex items-center justify-between pa-4 gap-4">
          <span class="text-sm">{{ t('canvasUI.canvasBuilder.components.name') }} ({{ selections?.length }})</span>
          <div class="flex items-center grow-0 basis-50">
            <span class="text-sm whitespace-nowrap">{{ t('canvasUI.canvasBuilder.components.sortBy') }}:</span>
            <CSelect
              class="select-none"
              :on-update:value="activeSortOptionChanged"
              :options="sortOptions"
              :value="activeSortOption"
            />
          </div>
        </div>
      </template>

      <CComponentListItem
        v-for="selection of selections"
        :key="`${selection.id}-${selection.type}`"
        class="mx-2 mb-2 cursor-pointer"
        :description="selection.description"
        :icon="selection.icon"
        :title="selection.title"
        :type="selection.type"
        @click="onAdd(selection)"
      />
    </NCard>
  </NModal>
</template>

<script lang="ts" setup>
import { orderBy } from 'lodash-es'
import { NCard, NInput, NModal, type SelectOption } from 'naive-ui'
import { computed, inject, nextTick, onMounted, ref, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasBlocks from '@canvas-builder/composables/useCanvasBlocks'
import useCollectionSelector from '@canvas-builder/composables/useCollectionSelector'
import { componentMeta } from '@canvas-builder/constants'
import {
  ComponentIcon,
  componentIconType,
  ComponentTypes,
  type BlockProps,
  type CanvasBuilderProps,
  type ComponentNode,
} from '@canvas-builder/types/canvas'
import type { FontAwesomeType } from '@canvas-builder/types/font-awesome.types'
import {
  getComponentDescription,
  getComponentKeywords,
  getComponentTitle,
  scrollToComponentById,
} from '@canvas-builder/util/canvas.util'
import type { CollectionPlayer } from '@collection-player/types/collection-player.type'
import { generateNewCollection } from '@collection-selector/utils/data.util'
import { useFetchApps } from '@lib/api/apps/apps.queries'
import CComponentListItem from '@lib/components/CComponentListItem/CComponentListItem.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CSelect from '@lib/components/CSelect/CSelect.vue'
import CTag from '@lib/components/CTag/CTag.vue'
import { loadCanvasAndDsrApps } from '@lib/composables/useInstalledApps'
import useThemeVars from '@lib/composables/useThemeVars'
import type { AppFile, EmbedLocation, PitcherInfo } from '@lib/types/app'
import type { CanvasComponentTypes, InstanceSettings } from '@lib/types/instanceSettings.types'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import { getFontAwesomeIconNameAndType } from '@lib/util/font.util'
import { fetchAll } from '@lib/util/network'
import { isMobile } from '@lib/util/userAgents'
import type { Canvas } from '@openapi'
import type { UiSelectCollectionPlayerRequest, UiSelectCollectionPlayerResponse } from '@sdk/api/modules/ui/types.ui'
defineProps<{
  baseStackingContext?: number
}>()

type ComponentSortValue = 'name-a-z' | 'name-z-a' | 'last-used'

const enum ComponentFilterTypesEnum {
  All = 'all',
  Apps = 'apps',
  Basic = 'basic',
  Blocks = 'blocks',
}

interface ComponentItem {
  app?: AppFile
  icon?: { name: string; type?: FontAwesomeType }
  description?: string
  id?: string
  keywords?: string[]
  lastUsed: string | undefined
  title: string
  type: ComponentTypes
}

const LOCAL_STORAGE_KEYS = {
  preferredSorting: 'cb_preferred_sorting',
  lastUsedComponentTimestamps: 'cb_last_used_component_timestamps',
}

const { t } = useI18n()
const themeVars = useThemeVars()
const {
  mode,
  activeCanvas,
  addMode,
  justAddedComponentId,
  singletonsInCanvasMap,
  parentIdByNodeId,
  componentNodesById,
  isSection,
  isBlock,
  setAddMode,
  addComponentAtParentId,
} = useCanvas()
const { getUserBlockOverrides } = useCanvasBlocks()
const inputRef = ref<HTMLInputElement | null>(null)
const { data: appList } = useFetchApps(loadCanvasAndDsrApps)
const fetchedBlocks = ref<Canvas[]>([])
const availableBlocks = computed(() =>
  // singleton blocks by block id
  fetchedBlocks.value.filter(
    (b) =>
      !Object.values(componentNodesById.value).some(
        (node) => node.type === ComponentTypes.Block && (node.data as BlockProps)?.id === b.id,
      ),
  ),
)
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)
const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
  'pitcherInfo',
  computed(() => ({
    accessToken: '',
    instanceId: '',
    apiOrigin: '',
    isAnyTypeOfAdmin: false,
  })),
)
const selectCollectionPlayerContent = inject<
  (payload?: UiSelectCollectionPlayerRequest) => Promise<UiSelectCollectionPlayerResponse>
>('selectCollectionPlayerContent')

const { selectContent } = useCollectionSelector(selectCollectionPlayerContent)

const listCanvases = inject<CanvasBuilderProps['listCanvases']>('listCanvases')
if (!listCanvases) {
  console.error('listCanvases is not provided')
}
const listBlocks = () =>
  fetchAll((p) => listCanvases!(p), {
    fields: 'id,name,description',
    page_size: 500,
    is_block: true,
    ordering: 'name',
    is_draft: false,
  })
const isSectionListTypeAllowed = (type: ComponentTypes) =>
  isSection.value || isBlock.value ? type !== ComponentTypes.SectionList : true

const isHtmlComponentAllowed = (type: ComponentTypes): boolean => {
  if (type !== ComponentTypes.Html) return true

  return !(launchDarkly.value?.show_only_preview_mode_for_html_component && !pitcherInfo.value?.isAnyTypeOfAdmin)
}

const isDynamicDataTableComponentAllowed = (type: ComponentTypes): boolean => {
  if (type !== ComponentTypes.DynamicDataTable) return true

  return !!launchDarkly.value?.enable_dynamic_data_table_component && !!pitcherInfo.value?.isAnyTypeOfAdmin
}

const isDynamicDataTableAllowedInScribble = (type: ComponentTypes, parentId?: ComponentNode['id'] | null): boolean => {
  if (type !== ComponentTypes.DynamicDataTable || !parentId) return true
  return (
    componentNodesById.value[parentId]?.type !== ComponentTypes.Scribble ||
    !!launchDarkly.value?.allow_dynamic_data_table_for_scribble_component
  )
}

const isHtmlAllowedInScribble = (type: ComponentTypes, parentId?: ComponentNode['id'] | null): boolean => {
  if (type !== ComponentTypes.Html || !parentId) return true
  return (
    componentNodesById.value[parentId]?.type !== ComponentTypes.Scribble ||
    !!launchDarkly.value?.allow_html_for_scribble_component
  )
}

const isGridTypeAllowedByDepth = (type: ComponentTypes, parentId?: ComponentNode['id'] | null) => {
  if (type !== ComponentTypes.Grid) return true

  let currentParentId = parentId
  let depth = 0

  while (currentParentId) {
    const parent = componentNodesById.value[currentParentId]
    if (parent?.type === ComponentTypes.Grid) {
      depth++
      if (depth > 1) return false // if we need to support more than 1 level, Grid.vue needs refactoring
    }
    currentParentId = parentIdByNodeId.value[parent.id]
  }

  return true
}

const isPageBreakTypeAllowed = (type: ComponentTypes): boolean => {
  if (type !== ComponentTypes.PageBreak) return true

  return (
    launchDarkly.value?.canvas_component_grouping === 'page_break' &&
    (pitcherSettings.value.canvas?.section_list_mode ?? 'pagination') !== 'collapsibles' &&
    !activeCanvas.value?.is_section
  )
}

const isPageTypeAllowed = (type: ComponentTypes): boolean => {
  if (type !== ComponentTypes.Page) return true

  return (
    launchDarkly.value?.canvas_component_grouping === 'page' &&
    (pitcherSettings.value.canvas?.section_list_mode ?? 'pagination') !== 'collapsibles' &&
    !activeCanvas.value?.is_section
  )
}

const isScribbleEnabled = (type: ComponentTypes): boolean => {
  if (type !== ComponentTypes.Scribble) return true

  return !!launchDarkly.value?.enable_scribble_component
}

const isCollectionPlayerTypeAllowed = (type: ComponentTypes): boolean => {
  if (type !== ComponentTypes.CollectionPlayer) return true

  return !!pitcherSettings.value?.canvas?.enable_collection_player_component
}

const isMultimediaAllowedInScribble = (type: ComponentTypes, parentId?: ComponentNode['id'] | null): boolean => {
  if (type !== ComponentTypes.Multimedia || !parentId) return true
  return (
    componentNodesById.value[parentId]?.type !== ComponentTypes.Scribble ||
    !!launchDarkly.value?.allow_multimedia_for_scribble_component
  )
}

const isEmbeddableAllowedInScribble = (type: ComponentTypes, parentId?: ComponentNode['id'] | null): boolean => {
  if (type !== ComponentTypes.Embeddable || !parentId) return true
  return (
    componentNodesById.value[parentId]?.type !== ComponentTypes.Scribble ||
    !!launchDarkly.value?.allow_embeddable_for_scribble_component
  )
}

const isTimelineTypeAllowed = (type: ComponentTypes): boolean => {
  if (type !== ComponentTypes.Timeline) return true

  return !!launchDarkly.value?.is_timeline_component_allowed
}

const isDataChartsEnabled = (type: ComponentTypes): boolean => {
  if (type !== ComponentTypes.DataCharts) return true

  return !!launchDarkly.value?.enable_data_charts_component
}

const isCustomDataTableComponentAllowed = (type: ComponentTypes): boolean => {
  if (type !== ComponentTypes.CustomDataTable) return true

  return !!launchDarkly.value?.enable_custom_data_tables && !!pitcherInfo.value?.isAnyTypeOfAdmin
}

const singletonDoesNotExistInCanvas = (type: ComponentTypes) =>
  componentMeta[type].singleton ? !singletonsInCanvasMap.value[type] : true

const getLastUsedComponentTimestamps = () => {
  try {
    const data = localStorage.getItem(LOCAL_STORAGE_KEYS.lastUsedComponentTimestamps)
    if (!data) return {}
    return JSON.parse(data)
  } catch (e) {
    return {}
  }
}

const activeSortOptionChanged = (value: ComponentSortValue) => {
  activeSortOption.value = value
  localStorage.setItem(LOCAL_STORAGE_KEYS.preferredSorting, value)
}

const activeFilter = ref(ComponentFilterTypesEnum.All)
const sortOptions = ref<SelectOption[]>([
  {
    label: t('canvasUI.canvasBuilder.components.sortOptions.lastUsed'),
    value: 'last-used',
  },
  {
    label: t('canvasUI.canvasBuilder.components.sortOptions.nameAscending'),
    value: 'name-a-z',
  },
  {
    label: t('canvasUI.canvasBuilder.components.sortOptions.nameDescending'),
    value: 'name-z-a',
  },
])

const lastUsedComponentTimestamps = getLastUsedComponentTimestamps()
const preferredSorting = (localStorage.getItem(LOCAL_STORAGE_KEYS.preferredSorting) as ComponentSortValue | null) ?? ''

const activeSortOption = ref<ComponentSortValue>(preferredSorting || 'last-used')
const search = ref('')

const componentAvailability = computed(() => pitcherSettings.value?.canvas?.component_availability)
const componentAvailabilityRoles = computed(() => pitcherSettings.value?.canvas?.component_availability_roles)
const isUserAdmin = computed(() => pitcherInfo.value?.isAnyTypeOfAdmin ?? false)

const isRoleBasedAvailabilityEnabled = computed(() => launchDarkly.value?.enable_role_based_component_availability)

const isComponentAvailableForCurrentUser = (componentType: ComponentTypes): boolean => {
  // Check if role-based availability is enabled via LaunchDarkly
  if (isRoleBasedAvailabilityEnabled.value && componentAvailabilityRoles.value) {
    const roleSettings = componentAvailabilityRoles.value[componentType as CanvasComponentTypes]
    if (roleSettings) {
      if (isUserAdmin.value) {
        return roleSettings.admin ?? true
      } else {
        return roleSettings.rep ?? true
      }
    }
  }

  // Fall back to the old component availability system
  if (componentAvailability.value) {
    return componentAvailability.value[componentType as CanvasComponentTypes] ?? true
  }

  // Default to available if no settings exist
  return true
}
const areBlocksEnabled = computed(() => launchDarkly.value?.enable_canvas_blocks && !isBlock.value && !isSection.value)

const shouldShowBlocksTab = computed(() => {
  // Don't show blocks tab if blocks aren't enabled or there are no available blocks
  if (!areBlocksEnabled.value || !availableBlocks.value.length) {
    return false
  }

  // Don't show blocks tab when adding to scribble since blocks aren't allowed as scribble children
  if (addMode.value && addMode.value.type === ComponentTypes.Scribble) {
    return false
  }

  return true
})
const hideApps = computed(
  () =>
    (!isComponentAvailableForCurrentUser(ComponentTypes.Embeddable) && launchDarkly.value?.allow_embeddable_hiding) ||
    !appList.value?.length ||
    (addMode.value &&
      addMode.value.type === ComponentTypes.Scribble &&
      !launchDarkly.value?.allow_embeddable_for_scribble_component),
)

const getIconByComponentType = (componentType: ComponentTypes) => {
  let name = ''

  if (componentType in ComponentIcon) {
    name = ComponentIcon[componentType as keyof typeof ComponentIcon]
  }

  let type: FontAwesomeType | undefined = undefined

  if (componentType in componentIconType) {
    type = componentIconType[componentType as keyof typeof componentIconType]
  }

  return {
    name,
    type,
  }
}

const selections = computed(() => {
  if (!addMode.value) return []

  const parentId = addMode.value.parentId
  const items = componentMeta[addMode.value.type].allowedChildren.filter(
    (type) =>
      isSectionListTypeAllowed(type) &&
      isHtmlComponentAllowed(type) &&
      isDynamicDataTableComponentAllowed(type) &&
      isDynamicDataTableAllowedInScribble(type, parentId) &&
      isCustomDataTableComponentAllowed(type) &&
      isHtmlAllowedInScribble(type, parentId) &&
      isEmbeddableAllowedInScribble(type, parentId) &&
      isGridTypeAllowedByDepth(type, parentId) &&
      isScribbleEnabled(type) &&
      isMultimediaAllowedInScribble(type, parentId) &&
      isPageBreakTypeAllowed(type) &&
      singletonDoesNotExistInCanvas(type) &&
      isPageTypeAllowed(type) &&
      isTimelineTypeAllowed(type) &&
      isCollectionPlayerTypeAllowed(type) &&
      isDataChartsEnabled(type) &&
      ![ComponentTypes.Block, ComponentTypes.Embeddable].includes(type),
  )

  // adding standard components to the components
  let components: ComponentItem[] = items.map((i) => ({
    description: getComponentDescription(i, t),
    keywords: getComponentKeywords(i, t),
    lastUsed: lastUsedComponentTimestamps[i] ?? undefined,
    title: getComponentTitle(i, t),
    type: i,
    icon: getIconByComponentType(i),
  }))

  // adding apps to the components
  if (
    appList.value?.length &&
    !hideApps.value &&
    componentMeta[addMode.value.type].allowedChildren.includes(ComponentTypes.Embeddable)
  )
    components.push(
      ...appList.value.map((i) => {
        const id = i.app_metadata?.name ?? i.id
        return {
          app: i,
          description: i.app_metadata?.description,
          icon: getFontAwesomeIconNameAndType(i.app_metadata?.icon || 'far fa-browser'),
          id,
          keywords: [],
          lastUsed: lastUsedComponentTimestamps[id] ?? undefined,
          title: i.app_metadata?.display_name ?? i.app_metadata?.name ?? i.name ?? '',
          type: ComponentTypes.Embeddable,
        }
      }),
    )

  if (
    areBlocksEnabled.value &&
    availableBlocks.value.length &&
    componentMeta[addMode.value.type].allowedChildren.includes(ComponentTypes.Block)
  ) {
    components.push(
      ...availableBlocks.value.map((b) => ({
        id: b.id,
        title: getComponentTitle(ComponentTypes.Block, t, { name: b.name }),
        description: getComponentDescription(ComponentTypes.Block, t, { description: b.metadata?.description }),
        icon: getFontAwesomeIconNameAndType(b.metadata?.icon || 'fas fa-objects-column'),
        type: ComponentTypes.Block,
        lastUsed: lastUsedComponentTimestamps[b.id] ?? undefined,
      })),
    )
  }

  // Filter components based on role-based availability settings
  components = components.filter((component) => {
    // Special handling for embeddable components and hideApps
    if (component.type === ComponentTypes.Embeddable && !hideApps.value) {
      return true
    }

    // Use the new role-based availability check
    return isComponentAvailableForCurrentUser(component.type)
  })

  // filter by active filters
  switch (activeFilter.value) {
    case ComponentFilterTypesEnum.Basic:
      components = components.filter((i) => i.type !== ComponentTypes.Embeddable && i.type !== ComponentTypes.Block)
      break
    case ComponentFilterTypesEnum.Apps:
      components = components.filter((i) => i.type === ComponentTypes.Embeddable)
      break
    case ComponentFilterTypesEnum.Blocks:
      components = components.filter((i) => i.type === ComponentTypes.Block)
      break
    default:
      break
  }

  // filter by search term
  if (search.value)
    components = components.filter(
      (i) =>
        i.title.toLowerCase().includes(search.value.toLowerCase()) ||
        i.keywords?.some((k) => k.toLowerCase().includes(search.value.toLowerCase())),
    )

  // sort results by active sort option
  return reorderComponentsList(components)
})

const reorderComponentsList = (items: ComponentItem[]) => {
  switch (activeSortOption.value) {
    case 'name-a-z':
      return orderBy(items, [(item) => item.title.toLowerCase()], ['asc'])
    case 'name-z-a':
      return orderBy(items, [(item) => item.title.toLowerCase()], ['desc'])
    case 'last-used':
      return orderBy(items, [(item) => item.lastUsed === undefined, 'lastUsed', 'title'], ['asc', 'desc', 'asc'])
    default:
      return items
  }
}

async function onBlockAdd(blockId?: string) {
  if (!blockId) {
    console.warn('Block id is required')
    return
  }

  let userOverrides = undefined
  if (mode.value !== 'admin') userOverrides = await getUserBlockOverrides(blockId)

  addComponentAtParentId(
    ComponentTypes.Block,
    addMode.value ? addMode.value.parentId : null,
    addMode.value ? addMode.value.afterSiblingId : null,
    {
      id: blockId,
      overrides: userOverrides ?? undefined,
    } satisfies BlockProps,
  )
}

const onAdd = async (selection: ComponentItem) => {
  if (!selection) return

  localStorage.setItem(
    LOCAL_STORAGE_KEYS.lastUsedComponentTimestamps,
    JSON.stringify({ ...lastUsedComponentTimestamps, [selection.id ?? selection.type]: new Date().toISOString() }),
  )

  if (selection.type === ComponentTypes.Block) {
    await onBlockAdd(selection.id)
  } else if (selection.type === ComponentTypes.Embeddable) {
    const app = selection.app as AppFile

    // move to a const file and change in all places
    const availableLocations = ['canvas', 'dsr'] satisfies EmbedLocation[]

    addComponentAtParentId(
      selection.type,
      addMode.value ? addMode.value.parentId : null,
      addMode.value ? addMode.value.afterSiblingId : null,
      {
        app_name: app.app_metadata?.name ?? '',
        width: app.app_metadata?.module?.canvas?.defaults?.dimensions?.width,
        height: app.app_metadata?.module?.canvas?.defaults?.dimensions?.height,
        src: app.content_url,
        type: 'app',
        embed_locations: availableLocations.filter((l) => app.app_metadata?.module?.[l]?.enabled),
        headless_locations: availableLocations.filter((l) => app.app_metadata?.module?.[l]?.headless),
        settings: app.app_metadata?.module?.canvas?.defaults?.settings ?? {},
        properties_by_embed_location: {
          canvas: { start_in_loading_mode: app.app_metadata?.module?.canvas?.start_in_loading_mode ?? false },
          dsr: { start_in_loading_mode: app.app_metadata?.module?.dsr?.start_in_loading_mode ?? false },
        },
        file: {
          id: app.id,
          name: app.name,
          content_url: app.content_url,
          content_type: app.content_type,
          content_extension: app.content_extension,
          thumbnail_url: app.thumbnail_url,
          content_length: app.content_length,
          content_thumbnails: null,
        },
      },
    )
  } else {
    let withData = undefined

    if (selection.type === ComponentTypes.CollectionPlayer) {
      withData = generateNewCollection(t)
    }

    addComponentAtParentId(
      selection.type,
      addMode.value ? addMode.value.parentId : null,
      addMode.value ? addMode.value.afterSiblingId : null,
      withData,
    )
  }

  scrollToComponentById(justAddedComponentId.value)

  setAddMode(false)

  if (selection.type === ComponentTypes.CollectionPlayer) {
    if (!justAddedComponentId.value) return

    await selectContent({
      id: justAddedComponentId.value,
      initialData: componentNodesById.value[justAddedComponentId.value]?.data as CollectionPlayer,
    })
  }
}

const handleClick = (value: ComponentFilterTypesEnum) => {
  activeFilter.value = activeFilter.value === value ? ComponentFilterTypesEnum.All : value
}

const handleClose = () => {
  activeFilter.value = ComponentFilterTypesEnum.All
}

onMounted(async () => {
  nextTick(() => {
    !isMobile && inputRef.value?.focus()
  })
  if (areBlocksEnabled.value) fetchedBlocks.value = await listBlocks()
})
</script>

<style lang="scss">
.cb-add-component-modal {
  width: 720px;
  height: clamp(350px, calc(100vh - 120px), 800px);
}

.cb-add-component-modal__category {
  .n-collapse-item {
    .n-collapse-item__content-wrapper {
      .n-collapse-item__content-inner {
        padding: 0;
      }
    }
  }
}

.cb-add-component-modal__card .n-card__content {
  max-height: clamp(100px, calc(100vh - 360px), 560px);
  overflow: auto;
}
</style>
